import React from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, Grid, IconButton, ListItemText } from '@mui/material';
import UpdateCronDialog from '../UpdateCronDialog';
import useModal from '@tenant/utility/hooks/useModal';
import { CONTAINER_APP_STATUS_ENUM } from '@tenant/utility/constants/enum';
import DockerAppTypes from 'constants/DockerAppTypes';
import { formatDateFromISO } from '@tenant/utility/helper/DateHelper';
import { inputFormatDateTime } from '@tenant/utility/constants/default';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BindDomainManualAction from 'pages/containerApp/Common/BindDomainManualAction';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DeleteDialog from '@tenant/core/App/Dialog/DeleteDialog';
import useUnbindDomain from 'redux/slices/containerApp/hooks/useUnbindDomain';
import { copyToClipboard } from '@tenant/utility/helper/Utils';
import { useQueryClient } from '@tanstack/react-query';
import ChangeVersionAction from 'pages/containerApp/Common/ChangeVersionAction';
import AllowedAccess from '@tenant/core/AppPermission/AllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';

const ContainerAppDescription = ({
  detail,
  appInstanceId,
  containerAppId,
  refetchDetail,
}) => {
  const queryClient = useQueryClient();
  const unbindDomain = useUnbindDomain();
  const { visible, onClose, onShow } = useModal();
  const {
    visible: visibleRemoveDomain,
    onClose: onCloseRemoveDomain,
    onShow: onShowRemoveDomain,
  } = useModal();

  const isConsoleApp =
    !!detail?.DeploymentType &&
    DockerAppTypes.find(
      (e) => e.Value.toString() === detail.DeploymentType.toString(),
    )?.IsCron;

  return (
    <Box
      sx={{
        '& .MuiButtonBase-root': {
          height: 16,
          width: 16,
          ml: 3,
          mr: 3,
          '& .MuiSvgIcon-root': {
            fontSize: 18,
          },
        },
      }}
    >
      <Grid container spacing={5}>
        <Grid item xs={6} sm={6} md={3}>
          <ListItemText
            primary='App Component Name'
            secondaryTypographyProps={{ component: 'div' }}
            secondary={<Box>{detail?.AppComponentName || '-'}</Box>}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <ListItemText
            primary='ID Reference'
            secondary={
              detail?.ContainerAppGuidName ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      mr: 1,
                    }}
                  >
                    {detail.ContainerAppGuidName}
                  </Box>
                  <IconButton
                    size='small'
                    title='Copy'
                    onClick={() =>
                      copyToClipboard(detail?.ContainerAppGuidName)
                    }
                  >
                    <ContentCopyIcon fontSize='inherit' />
                  </IconButton>
                </Box>
              ) : (
                '-'
              )
            }
            secondaryTypographyProps={{ component: 'div' }}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <ListItemText
            primary='Image'
            secondary={
              <Box display={'flex'} alignItems={'center'}>
                {detail?.Image || '-'}

                {!!detail?.Id && (
                  <AllowedAccess
                    permissionIds={[
                      AuthorizePermission.APP_INSTANCE_CONTAINER_UPDATE,
                    ]}
                  >
                    <ChangeVersionAction
                      imageId={detail?.DockerImageMetaDataId}
                      imageName={detail?.Image}
                      appInstanceId={appInstanceId}
                      containerId={containerAppId}
                      refetchDetail={refetchDetail}
                    />
                  </AllowedAccess>
                )}
              </Box>
            }
            secondaryTypographyProps={{ component: 'div' }}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <ListItemText
            primary='Status'
            secondaryTypographyProps={{
              sx: {
                color:
                  detail?.Status === CONTAINER_APP_STATUS_ENUM['Running']
                    ? 'success.main'
                    : 'error.main',
                textTransform: 'uppercase',
              },
            }}
            secondary={
              detail?.Status
                ? Object.entries(CONTAINER_APP_STATUS_ENUM).find(
                    ([key, value]) => value === detail.Status,
                  )[0]
                : '-'
            }
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <ListItemText
            primary='Deployment Type'
            secondary={
              detail?.DeploymentType
                ? DockerAppTypes.find(
                    (e) =>
                      e.Value.toString() === detail.DeploymentType.toString(),
                  ).Description
                : '-'
            }
          />
        </Grid>

        {!!detail?.DeploymentType && isConsoleApp && (
          <Grid item xs={6} sm={6} md={3}>
            <UpdateCronDialog
              visible={visible}
              handleClose={onClose}
              initialValues={{ Cron: detail?.Cron }}
              appInstanceId={appInstanceId}
              containerAppId={containerAppId}
            />
            <ListItemText
              primary='Cron'
              secondary={
                <Box
                  component='span'
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                  onClick={onShow}
                >
                  {detail?.Cron || '-'}
                </Box>
              }
            />
          </Grid>
        )}
      </Grid>

      {!isConsoleApp && (
        <>
          <Box mt={3} mb={3}>
            <Divider />
          </Box>

          <Grid container spacing={5}>
            <Grid item xs={6} sm={6} md={6}>
              <ListItemText
                primary='Custom Domain'
                secondary={
                  detail?.CustomDomainName ? (
                    <>
                      <Box
                        component={'a'}
                        href={'https://' + detail?.CustomDomainName}
                        target='_blank'
                        mr={2}
                      >
                        {detail?.CustomDomainName}
                      </Box>
                      <IconButton
                        size='small'
                        title='Copy'
                        onClick={() =>
                          copyToClipboard(detail?.CustomDomainName)
                        }
                      >
                        <ContentCopyIcon fontSize='inherit' />
                      </IconButton>
                      <IconButton
                        size='small'
                        color='error'
                        title='Delete domain'
                        sx={{
                          '& .MuiSvgIcon-root': {
                            fontSize: '22px !important',
                          },
                        }}
                        onClick={onShowRemoveDomain}
                      >
                        <DeleteForeverIcon />
                      </IconButton>
                    </>
                  ) : detail?.Status ===
                    CONTAINER_APP_STATUS_ENUM['Running'] ? (
                    <BindDomainManualAction
                      appInstanceId={appInstanceId}
                      containerId={containerAppId}
                      refetchDetail={refetchDetail}
                    />
                  ) : (
                    '-'
                  )
                }
                secondaryTypographyProps={{ component: 'div' }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <ListItemText
                primary='Fully Qualified Domain'
                secondary={
                  detail?.FullyQualifiedDomainName ? (
                    <>
                      <Box
                        component={'a'}
                        href={'https://' + detail?.FullyQualifiedDomainName}
                        target='_blank'
                        mr={2}
                      >
                        {detail?.FullyQualifiedDomainName}
                      </Box>
                      <IconButton
                        size='small'
                        title='Copy'
                        onClick={() =>
                          copyToClipboard(detail?.FullyQualifiedDomainName)
                        }
                      >
                        <ContentCopyIcon fontSize='inherit' />
                      </IconButton>
                    </>
                  ) : (
                    '-'
                  )
                }
              />
            </Grid>
          </Grid>
        </>
      )}

      <Box mt={3} mb={3}>
        <Divider />
      </Box>

      <Grid container spacing={5}>
        <Grid item xs={6} sm={6} md={3}>
          <ListItemText
            primary='CreatedAt'
            secondary={
              detail?.CreatedAt
                ? formatDateFromISO(detail?.CreatedAt, inputFormatDateTime)
                : '-'
            }
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <ListItemText
            primary='UpdatedAt'
            secondary={
              detail?.UpdatedAt
                ? formatDateFromISO(detail.UpdatedAt, inputFormatDateTime)
                : '-'
            }
          />
        </Grid>
      </Grid>

      {!!detail.AzureContainerAppUrl && (
        <>
          <Box mt={3} mb={3}>
            <Divider />
          </Box>

          <Grid container spacing={5}>
            <Grid item xs={12} sm={12} md={12}>
              <ListItemText
                primary='Azure Container Url'
                secondary={
                  <Box
                    component='a'
                    href={detail.AzureContainerAppUrl}
                    target='_blank'
                    sx={{
                      textDecoration: 'none',
                      color: 'primary.main',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      width: '100%',
                      display: 'block',
                    }}
                  >
                    {detail.AzureContainerAppUrl}
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </>
      )}

      <DeleteDialog
        action='Delete Domain'
        visible={visibleRemoveDomain}
        loading={unbindDomain.isPending}
        handleClose={onCloseRemoveDomain}
        handleSave={() => {
          unbindDomain.mutate(
            { appInstanceId, containerAppId },
            {
              onSuccess() {
                onCloseRemoveDomain();
                queryClient.invalidateQueries([
                  'app-instance-detail-' + appInstanceId,
                ]);
              },
            },
          );
        }}
      />
    </Box>
  );
};

export default React.memo(ContainerAppDescription);

ContainerAppDescription.propTypes = {
  detail: PropTypes.object,
  appInstanceId: PropTypes.string,
  containerAppId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  refetchDetail: PropTypes.func,
};
